import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useState,
} from 'react';
import {
  VillageDeal,
  VillageExtra,
  VillageFutureBooking,
  VillagePricePeriod,
  VillagePriceVariants,
} from '@/services/types';

export type CustomExtra = {
  selectedQuantity: number;
  freeQuantity: number;
  computedPrice: number;
} & VillageExtra;

type BookSideWidgetInfos = {
  start: Date | null;
  end: Date | null;
  quantities: {
    adults: number;
    children: number;
    infants: number;
  };
};

export interface VillageContextProps {
  totalGuests: number;
  setTotalGuests: Dispatch<SetStateAction<number>>;
  visibleDeal: VillageDeal | undefined;
  setVisibleDeal: Dispatch<SetStateAction<VillageDeal | undefined>>;
  totalBedrooms: number;
  setTotalBedrooms: Dispatch<SetStateAction<number>>;
  extras: CustomExtra[];
  selectedDates: {
    start: Date | null;
    end: Date | null;
  };
  selectedPeriod: VillagePricePeriod | undefined;
  priceVariant: VillagePriceVariants | undefined;
  futureBookings: VillageFutureBooking[];
  setFutureBookings: Dispatch<SetStateAction<VillageFutureBooking[]>>;
  inWishList: boolean;
  handleWishList: (inWishList: boolean) => void;
  handleSelectDates: (value: {start: Date | null; end: Date | null}) => void;
  handleSelectPeriod: (period: VillagePricePeriod) => void;
  handleSelectPriceVariant: (variant: VillagePriceVariants) => void;
  handleExtras: (extras: CustomExtra[]) => void;
  setCalendarPromotionTitle: Dispatch<SetStateAction<string | undefined>>;
  calendarPromotionTitle: string | undefined;
  bookSideWidgetInfos: BookSideWidgetInfos;
  setBookSideWidgetInfos: Dispatch<SetStateAction<BookSideWidgetInfos>>;
}

interface VillageProviderProps {
  children: ReactNode;
}

const VillageContext = createContext<VillageContextProps>(
  {} as VillageContextProps,
);

export const bookSideWidgetInfosDefaultValues = {
  start: null,
  end: null,
  quantities: {
    adults: 0,
    children: 0,
    infants: 0,
  },
};

export const VillageContextProvider: FC<VillageProviderProps> = ({
  children,
}) => {
  const [selectedDates, setSelectedDates] = useState<{
    start: Date | null;
    end: Date | null;
  }>({start: null, end: null});
  const [totalGuests, setTotalGuests] = useState(0);
  const [totalBedrooms, setTotalBedrooms] = useState(0);
  const [visibleDeal, setVisibleDeal] = useState<VillageDeal | undefined>();
  const [selectedPeriod, setSelectedPeriod] = useState<VillagePricePeriod>();
  const [priceVariant, setPriceVariant] = useState<VillagePriceVariants>();
  const [extras, setExtras] = useState<CustomExtra[]>([]);
  const [inWishList, setInWishList] = useState(false);
  const [calendarPromotionTitle, setCalendarPromotionTitle] =
    useState<string>();
  const [bookSideWidgetInfos, setBookSideWidgetInfos] =
    useState<BookSideWidgetInfos>(bookSideWidgetInfosDefaultValues);
  const [futureBookings, setFutureBookings] = useState<VillageFutureBooking[]>(
    [],
  );

  function handleSelectDates(value: {start: Date | null; end: Date | null}) {
    setSelectedDates(value);
  }

  function handleSelectPeriod(period: VillagePricePeriod) {
    setSelectedPeriod(period);
  }

  function handleSelectPriceVariant(variant: VillagePriceVariants) {
    setPriceVariant(variant);
  }

  function handleWishList(inWishList: boolean) {
    setInWishList(inWishList);
  }

  function handleExtras(extras: CustomExtra[]) {
    setExtras(extras);
  }

  return (
    <VillageContext.Provider
      value={{
        totalGuests,
        setTotalGuests,
        visibleDeal,
        setVisibleDeal,
        totalBedrooms,
        setTotalBedrooms,
        selectedDates,
        selectedPeriod,
        priceVariant,
        inWishList,
        extras,
        futureBookings,
        setFutureBookings,
        handleWishList,
        handleSelectDates,
        handleSelectPeriod,
        handleSelectPriceVariant,
        handleExtras,
        calendarPromotionTitle,
        setCalendarPromotionTitle,
        bookSideWidgetInfos,
        setBookSideWidgetInfos,
      }}>
      {children}
    </VillageContext.Provider>
  );
};

export default VillageContext;
